import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Eye, EyeOff, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { resetPasswordApi, AuthApiError } from "@/services/auth.service";
import { PasswordStrengthChecker } from "@/modules/auth/ui/components/PasswordStrengthChecker";

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token") ?? "";
  const email = searchParams.get("email") ?? "";
  const isInvalidLink = !token || !email;

  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [isPasswordStrong, setIsPasswordStrong] = useState(false);

  // ── Validation locale ─────────────────────────────────────────────────────
  const validate = (): boolean => {
    if (password && !isPasswordStrong) {
      setError("Votre mot de passe doit respecter toutes les exigences de sécurité.");
      return false;
    }
    if (password !== passwordConfirmation) {
      setError("Les mots de passe ne correspondent pas.");
      return false;
    }
    return true;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    if (!validate()) return;

    setIsLoading(true);

    try {
      await resetPasswordApi({
        email,
        password,
        password_confirmation: passwordConfirmation,
        token,
      });
      setIsSuccess(true);
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (Object.keys(err.fieldErrors).length > 0) {
          setFieldErrors(err.fieldErrors);
        } else {
          setError(err.message);
        }
      } else {
        setError("Une erreur est survenue. Veuillez réessayer.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4 font-sans">
      <div className="max-w-md w-full bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-gray-100">
        {isInvalidLink ? (
          <div className="text-center space-y-5">
            <XCircle size={48} className="text-red-500 mx-auto" />
            <h1 className="text-2xl font-black text-[#003366]">Lien invalide</h1>
            <p className="text-gray-500 text-sm">
              Ce lien de réinitialisation est invalide ou a expiré. Veuillez refaire une demande.
            </p>
            <button
              type="button"
              onClick={() => navigate("/forgot-password")}
              className="w-full bg-[#003366] text-white rounded-xl py-4 font-black text-xs uppercase tracking-[0.2em] hover:bg-black transition-all"
            >
              Refaire une demande
            </button>
          </div>
        ) : isSuccess ? (
          <div className="text-center space-y-5">
            <CheckCircle2 size={48} className="text-emerald-500 mx-auto" />
            <h1 className="text-2xl font-black text-[#003366]">Mot de passe réinitialisé !</h1>
            <p className="text-gray-500 text-sm">
              Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter.
            </p>
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="w-full bg-[#003366] text-white rounded-xl py-4 font-black text-xs uppercase tracking-[0.2em] hover:bg-black transition-all"
            >
              Se connecter
            </button>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-black text-[#003366] mb-2">Nouveau mot de passe</h1>
            <p className="text-gray-400 text-sm mb-8">
              Choisissez un nouveau mot de passe pour votre compte.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Password */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Nouveau mot de passe
                </label>
                <div className="relative group">
                  <Lock
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-500 transition-colors"
                    size={18}
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-12 pr-12 py-3.5 outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 focus:bg-white transition-all text-gray-900 font-semibold disabled:opacity-50"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {fieldErrors.password && (
                  <p className="text-sm text-red-500 font-semibold">{fieldErrors.password[0]}</p>
                )}
                {password && (
                  <div className="mt-3 p-4 bg-gray-50/50 rounded-xl border border-gray-100">
                    <PasswordStrengthChecker
                      password={password}
                      onValidityChange={setIsPasswordStrong}
                    />
                  </div>
                )}
              </div>

              {/* Confirmation */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Confirmer le mot de passe
                </label>
                <div className="relative group">
                  <Lock
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-500 transition-colors"
                    size={18}
                  />
                  <input
                    type={showConfirmation ? "text" : "password"}
                    placeholder="••••••••"
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    disabled={isLoading}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-12 pr-12 py-3.5 outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 focus:bg-white transition-all text-gray-900 font-semibold disabled:opacity-50"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmation(!showConfirmation)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-600"
                  >
                    {showConfirmation ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {fieldErrors.password_confirmation && (
                  <p className="text-sm text-red-500 font-semibold">
                    {fieldErrors.password_confirmation[0]}
                  </p>
                )}
              </div>

              {/* General error */}
              {error && (
                <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm font-bold text-red-600">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#003366] text-white rounded-xl py-4 font-black text-xs uppercase tracking-[0.2em] hover:bg-black transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Réinitialisation…
                  </>
                ) : (
                  "Réinitialiser le mot de passe"
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
